import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { Grid, Select, Td, Tr, VStack } from "@chakra-ui/react";
import React from "react";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import EntityProfileField from "../../../../entityProfileModal/components/EntityProfileField";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import EntityProfileTable from "../../../../entityProfileModal/components/EntityProfileTable";
import { EntityProfileSummaryBlock as SummaryBlock } from "../../../../entityProfileModal/components/EntityProfileSummary";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import { formatCaregiverProfileValue } from "../../../caregiverProfileModal.utils";
import {
  useCaregiverSuppliesShipments,
  useUpdateCaregiverSupplySizes,
} from "./CaregiverProfileSuppliesShipmentsSection.hooks";

type Props = {
  caregiver: CaregiverProfileCaregiver;
};

const gloveSizeOptions: Array<NonNullable<CaregiverProfileCaregiver["glovesSize"]>> = [
  "S",
  "M",
  "L",
  "XL",
];
const scrubSizeOptions: Array<NonNullable<CaregiverProfileCaregiver["scrubsSize"]>> = [
  "S",
  "M",
  "L",
  "XL",
  "XXL",
];

function formatDateTime(value: unknown) {
  return formatCaregiverProfileValue(value) === ""
    ? ""
    : value instanceof Object
    ? dateFormatter.toDateTime(value as never)
    : String(value);
}

export default function CaregiverProfileSuppliesShipmentsSection(props: Props) {
  const shipmentsQuery = useCaregiverSuppliesShipments(props.caregiver.id);
  const updateSizesMutation = useUpdateCaregiverSupplySizes(props.caregiver.id);
  const [isEditing, setIsEditing] = React.useState(false);
  const [glovesSize, setGlovesSize] = React.useState(props.caregiver.glovesSize);
  const [scrubsSize, setScrubsSize] = React.useState(props.caregiver.scrubsSize);
  const shipments = shipmentsQuery.data?.shipments ?? [];

  React.useEffect(() => {
    if (isEditing) {
      return;
    }

    setGlovesSize(props.caregiver.glovesSize);
    setScrubsSize(props.caregiver.scrubsSize);
  }, [isEditing, props.caregiver.glovesSize, props.caregiver.scrubsSize]);

  return (
    <VStack alignItems="stretch" spacing={4}>
      <EntityProfileEditModeButton
        isDisabled={updateSizesMutation.isPending}
        isEditing={isEditing}
        label="Edit"
        onClick={() => setIsEditing((currentValue) => !currentValue)}
      />

      <SummaryBlock title="Supply sizes">
        <Grid gap={4} py={4} templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}>
          {isEditing ? (
            <>
              <Select
                value={glovesSize ?? ""}
                onChange={(event) => {
                  const nextGlovesSize = event.target.value as NonNullable<
                    CaregiverProfileCaregiver["glovesSize"]
                  >;

                  setGlovesSize(nextGlovesSize);
                  updateSizesMutation.mutate({ glovesSize: nextGlovesSize });
                }}
              >
                {gloveSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    Gloves Size: {size}
                  </option>
                ))}
              </Select>
              <Select
                value={scrubsSize ?? ""}
                onChange={(event) => {
                  const nextScrubsSize = event.target.value as NonNullable<
                    CaregiverProfileCaregiver["scrubsSize"]
                  >;

                  setScrubsSize(nextScrubsSize);
                  updateSizesMutation.mutate({ scrubsSize: nextScrubsSize });
                }}
              >
                {scrubSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    Scrubs Size: {size}
                  </option>
                ))}
              </Select>
            </>
          ) : (
            <>
              <EntityProfileField
                label="Gloves Size"
                value={formatCaregiverProfileValue(props.caregiver.glovesSize)}
              />
              <EntityProfileField
                label="Scrubs Size"
                value={formatCaregiverProfileValue(props.caregiver.scrubsSize)}
              />
            </>
          )}
        </Grid>
      </SummaryBlock>

      <SummaryBlock title="Shipments">
        <EntityProfileQuerySection
          errorLabel="Could not load caregiver supplies shipments."
          isError={shipmentsQuery.isError}
          isLoading={shipmentsQuery.isLoading}
          loadingLabel="Loading caregiver supplies shipments"
          onRetry={() => void shipmentsQuery.refetch()}
        >
          <EntityProfileTable
            columns={[
              "Supply Shipment Date",
              "Supply Arrival Date",
              "Caregiver Shipment Confirmation",
            ]}
          >
            {shipments.map((shipment) => (
              <Tr key={shipment.id}>
                <Td>{formatDateTime(shipment.shipmentDate)}</Td>
                <Td>{formatDateTime(shipment.arrivalDate)}</Td>
                <Td>
                  {shipment.caregiverConfirmation ? (
                    <CheckIcon color={entityProfileModalColors.success} />
                  ) : (
                    <CloseIcon color={entityProfileModalColors.danger} />
                  )}
                </Td>
              </Tr>
            ))}
          </EntityProfileTable>
        </EntityProfileQuerySection>
      </SummaryBlock>
    </VStack>
  );
}
